import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowUpRight, Search, Bookmark, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Section, Badge, AnimatedCard } from "@/components/ui/shared";
import { useSEO } from "@/hooks/useSEO";
import { useBookmarks } from "@/lib/bookmarks";
import { useToast } from "@/components/ui/Toast";
import { SkeletonCardGrid } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/States";
import { errorMessage, useMisconceptions, useMisconception, useClarifierFilters, usePageView } from "@/lib/api";
import type { MisconceptionCategory, MisconceptionSummary } from "@/lib/api";
import { MisconceptionEvidence, MisconceptionFeedback } from "@/components/clarifier/MisconceptionEvidence";
import { misconceptionPath } from "@/lib/paths";
import { useDebounce } from "@/hooks/useDebounce";

/* ============================================
   Severity Badge Colors
   ============================================ */

const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  low: "bg-blue-100 text-blue-700 border-blue-200",
};

/* ============================================
   Expanded Detail (fetched on expand)
   ============================================ */

function MisconceptionExpandedDetail({ id }: { id: string }) {
  const { data: detail, isLoading, isError, error } = useMisconception(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 gap-2 text-[var(--color-ink-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading evidence…</span>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="text-center py-6 text-sm text-[var(--color-ink-muted)]">
        {errorMessage(error, "Failed to load clarification details.")}
      </div>
    );
  }

  return <MisconceptionEvidence detail={detail} />;
}

/* ============================================
   Misconception Card
   ============================================ */

function MisconceptionCard({
  item, index, isExpanded, onToggle,
}: {
  item: MisconceptionSummary;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  const bmId = `clarifier-${item.id}`;
  const saved = isBookmarked(bmId);

  const handleBookmark = () => {
    const added = toggle({
      id: bmId,
      type: "clarifier",
      title: item.claim,
      subtitle: item.category,
      route: misconceptionPath(item),
    });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  return (
    <AnimatedCard delay={index < 8 ? index * 0.06 : 0}>
      <div id={item.id} className="card-elevated overflow-hidden scroll-mt-24">
        <div className="flex items-start">
        <button onClick={onToggle} aria-expanded={isExpanded} className="flex-1 min-w-0 p-5 md:p-6 pe-2 text-left">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <Badge value={item.category} />
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SEVERITY_COLORS[item.severity] ?? SEVERITY_COLORS.medium}`}>
                  {item.severity}
                </span>
              </div>
              <h3 className="font-[var(--font-display)] text-lg font-semibold text-[var(--color-midnight)]">
                "{item.claim}"
              </h3>
              <p className="text-sm text-[var(--color-ink-muted)] mt-1.5 line-clamp-2">{item.summary}</p>
            </div>
            {isExpanded ? <ChevronUp className="w-5 h-5 shrink-0 text-[var(--color-ink-muted)]" aria-hidden /> : <ChevronDown className="w-5 h-5 shrink-0 text-[var(--color-ink-muted)]" aria-hidden />}
          </div>
        </button>
        <button onClick={handleBookmark} aria-pressed={saved} aria-label={saved ? "Remove bookmark" : "Bookmark"}
          className={`my-5 md:my-6 p-1.5 rounded-lg transition-colors ${saved ? "text-[var(--color-gold)]" : "text-[var(--color-ink-muted)]/40 hover:text-[var(--color-gold)]"}`}>
          <Bookmark className={`w-4 h-4 ${saved ? "fill-current" : ""}`} />
        </button>
        <Link to={misconceptionPath(item)} aria-label={`Open the full answer: ${item.title}`} title="Open the full answer"
          className="my-5 md:my-6 me-5 md:me-6 p-1.5 rounded-lg text-[var(--color-ink-muted)]/40 hover:text-[var(--color-gold)] transition-colors">
          <ArrowUpRight className="w-4 h-4" />
        </Link>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-6 md:px-6 border-t border-[var(--color-cream-dark)]">
                <MisconceptionExpandedDetail id={item.id} />

                <MisconceptionFeedback item={item} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AnimatedCard>
  );
}

/* ============================================
   Clarifier Page
   ============================================ */

export default function ClarifierPage() {
  useSEO("Misconceptions About Islam, Answered", "Common misconceptions about Islam answered with evidence from the Quran, authentic hadith and historical scholarship, each reviewed by qualified scholars.");

  const [activeCat, setActiveCat] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { hash } = useLocation();
  const [expandedId, setExpandedId] = useState<string | null>(hash ? hash.slice(1) : null);
  const search = useDebounce(searchQuery.trim());

  // API hooks
  const { data: listResponse, isLoading, isError, error, refetch } = useMisconceptions({
    category: activeCat === "all" ? undefined : (activeCat as MisconceptionCategory),
    search: search || undefined,
    page_size: 100,
  });

  // Opening /clarifier#<id> (e.g. from a bookmark) expands and scrolls to that card.
  useEffect(() => {
    if (!hash || !listResponse) return;
    const id = hash.slice(1);
    setExpandedId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, listResponse]);
  const { data: filtersResponse } = useClarifierFilters();

  usePageView("clarifier");

  const misconceptions: MisconceptionSummary[] = listResponse?.misconceptions ?? [];
  const categories = useMemo(() => filtersResponse?.categories ?? [], [filtersResponse]);

  const categoryPills = useMemo(() => {
    const all = [{ key: "all", label: "All Topics", count: categories.reduce((s, c) => s + c.count, 0) }];
    return all.concat(categories.map((c) => ({
      key: c.category,
      label: c.category.charAt(0).toUpperCase() + c.category.slice(1),
      count: c.count,
    })));
  }, [categories]);

  const totalMisconceptions = listResponse?.total ?? misconceptions.length;

  return (
    <main className="pt-16">
      <div className="bg-gradient-to-b from-rose-950 to-[var(--color-midnight)] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-rose-300 text-sm font-semibold uppercase tracking-[0.2em]">Evidence-Based Responses</span>
            <h1 className="mt-3 font-[var(--font-display)] text-4xl md:text-5xl font-bold text-[var(--color-cream)]">Misconception Clarifier</h1>
            <p className="mt-4 text-[var(--color-cream)]/60 max-w-2xl mx-auto">
              Common misconceptions about Islam, addressed with primary sources from the Quran, authenticated hadith, and peer-reviewed historical scholarship.
            </p>
            <div className="flex justify-center gap-6 mt-6 text-[var(--color-cream)]/40 text-xs">
              <span>{totalMisconceptions} misconceptions</span>
              <span>Verified primary sources</span>
            </div>
          </motion.div>
        </div>
      </div>

      <Section>
        <div className="max-w-3xl mx-auto">
          {/* Search */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-ink-muted)]" />
              <input type="search" aria-label="Search misconceptions" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search misconceptions..."
                className="w-full pl-11 pr-4 py-3 rounded-full border border-[var(--color-cream-dark)] bg-white text-sm focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-200 transition-all" />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categoryPills.map((cat) => (
              <button key={cat.key} onClick={() => setActiveCat(cat.key)}
                className={`pill ${activeCat === cat.key ? "active" : ""}`}>
                {cat.label}
                {cat.count > 0 && <span className="text-[10px] opacity-60 ml-1">({cat.count})</span>}
              </button>
            ))}
          </div>

          {/* Cards */}
          {isLoading ? (
            <SkeletonCardGrid count={4} />
          ) : isError ? (
            <ErrorState error={error} onRetry={refetch} />
          ) : (
            <div className="space-y-4">
              {misconceptions.length > 0 ? (
                misconceptions.map((item, i) => (
                  <MisconceptionCard key={item.id} item={item} index={i}
                    isExpanded={expandedId === item.id}
                    onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)} />
                ))
              ) : (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-[var(--color-cream-dark)] mx-auto mb-3" />
                  <p className="text-[var(--color-ink-muted)]">No matching misconceptions found.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Section>
    </main>
  );
}
